import { useQuery } from "@tanstack/react-query"
import { CalendarCheck, ChevronLeft, ChevronRight, Clock, Users } from "lucide-react"
import { useRef, useState } from "react"
import { Link } from "react-router"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"
import { comoFecha } from "@/lib/fechas"

type Permiso = {
  documento_id: number
  codigo: string
  persona_id: number
  persona: string
  tipo: string
  desde: string | null
  hasta: string | null
  dias: number
  por_horas: boolean
  horario: string | null
  motivo: string | null
  estado: string
  observacion: string | null
  creado_en: string
}

type ResumenPersona = {
  persona_id: number
  persona: string
  solicitudes: number
  pendientes: number
  aprobados: number
  dias_aprobados: number
  por_tipo: Record<string, number>
}

type Permisos = {
  anio: number
  pendientes: number
  dias_aprobados: number
  permisos: Permiso[]
  por_persona: ResumenPersona[]
}

const ESTADO: Record<string, { texto: string; tono: Tono }> = {
  SOLICITADO: { texto: "Por resolver", tono: "aviso" },
  APROBADO: { texto: "Aprobado", tono: "exito" },
  EMITIDO: { texto: "Aprobado", tono: "exito" },
  RECHAZADO: { texto: "Rechazado", tono: "peligro" },
  ANULADO: { texto: "Anulado", tono: "neutro" },
}

/**
 * Los permisos del personal, juntos (28/10, minuta «Estructura de la Compañía», §2.2):
 * quién pidió qué, cuántos días hábiles, qué falta por resolver y cuánto lleva cada uno
 * en el año.
 *
 * ⚠ **No es otro formulario.** El empleado lo pide en «Mis solicitudes» (plantilla
 * «Solicitud de permiso») y se aprueba o rechaza en la página del documento, con su
 * motivo: aquí sólo se ve todo junto y se llega a cada uno.
 */
export function PermisosTH() {
  const [anio, setAnio] = useState(() => new Date().getFullYear())
  const consulta = useQuery({
    queryKey: ["permisos-th", anio],
    queryFn: () => api.get<Permisos>(`/api/v1/talento-humano/permisos?anio=${anio}`),
  })

  const vista = useRef<HTMLElement | null>(null)

  return (
    <section ref={vista} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Boton tamano="sm" aria-label="Año anterior" onClick={() => setAnio((a) => a - 1)}>
            <ChevronLeft size={16} aria-hidden={true} />
          </Boton>
          <span className="text-sm font-semibold tabular-nums">{anio}</span>
          <Boton tamano="sm" aria-label="Año siguiente" onClick={() => setAnio((a) => a + 1)}>
            <ChevronRight size={16} aria-hidden={true} />
          </Boton>
        </div>
        <div className="flex flex-wrap items-start gap-3">
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Se piden en «Mis solicitudes» → Solicitud de permiso.
          </p>
          {consulta.data && (
            <Exportar titulo="Permisos del personal" subtitulo={`Año ${anio}`} contenido={vista} />
          )}
        </div>
      </div>

      {consulta.isPending && <EstadoCarga etiqueta="Cargando los permisos" />}
      {consulta.isError && (
        <EstadoError
          titulo="No se pudieron cargar los permisos"
          error={consulta.error}
          onReintentar={() => consulta.refetch()}
        />
      )}
      {consulta.data && <CuerpoPermisos datos={consulta.data} />}
    </section>
  )
}

function CuerpoPermisos({ datos }: { datos: Permisos }) {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-3">
        <TarjetaKpi
          titulo="Por resolver"
          detalle="Solicitudes que esperan aprobación"
          Icono={Clock}
          color="var(--warning)"
          cantidad={datos.pendientes}
        />
        <TarjetaKpi
          titulo="Días aprobados"
          detalle="Hábiles, de lunes a viernes"
          Icono={CalendarCheck}
          color="var(--success)"
          cantidad={datos.dias_aprobados}
        />
        <TarjetaKpi
          titulo="Personas con permisos"
          detalle={`En ${datos.anio}`}
          Icono={Users}
          color="var(--info)"
          cantidad={datos.por_persona.length}
        />
      </div>

      <Tabla etiqueta="Solicitudes de permiso">
        <Cabecera>
          <Th>Quién y qué</Th>
          <Th>Desde</Th>
          <Th>Hasta</Th>
          <Th className="text-right">Días</Th>
          <Th>Estado</Th>
          <Th>
            <span className="sr-only">Abrir</span>
          </Th>
        </Cabecera>
        <Cuerpo>
          {datos.permisos.length === 0 && (
            <TablaVacia colSpan={6}>No hay solicitudes de permiso en {datos.anio}.</TablaVacia>
          )}
          {datos.permisos.map((p) => {
            const e = ESTADO[p.estado] ?? { texto: p.estado, tono: "neutro" as const }
            return (
              <Fila key={p.documento_id}>
                <Td>
                  <div className="font-medium">{p.persona}</div>
                  <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    {p.tipo}
                    {p.por_horas && ` · por horas (${p.horario})`}
                    {p.motivo && ` · ${p.motivo}`}
                  </div>
                </Td>
                <Td className="tabular-nums">{comoFecha(p.desde)}</Td>
                <Td className="tabular-nums">{comoFecha(p.hasta)}</Td>
                <Td className="text-right tabular-nums">{p.dias}</Td>
                <Td>
                  <Insignia tono={e.tono}>{e.texto}</Insignia>
                </Td>
                <Td>
                  <Link
                    to={`/talento-humano/documento/${p.documento_id}`}
                    className="foco-cristal text-sm underline"
                  >
                    {p.estado === "SOLICITADO" ? "Resolver" : "Ver"}
                  </Link>
                </Td>
              </Fila>
            )
          })}
        </Cuerpo>
      </Tabla>

      {datos.por_persona.length > 0 && (
        <Tabla etiqueta="Permisos por persona en el año">
          <Cabecera>
            <Th>Persona</Th>
            <Th className="text-right">Solicitudes</Th>
            <Th className="text-right">Por resolver</Th>
            <Th className="text-right">Días aprobados</Th>
            <Th>Por tipo</Th>
          </Cabecera>
          <Cuerpo>
            {datos.por_persona.map((r) => (
              <Fila key={r.persona_id}>
                <Td>{r.persona}</Td>
                <Td className="text-right tabular-nums">{r.solicitudes}</Td>
                <Td className="text-right tabular-nums">{r.pendientes}</Td>
                <Td className="text-right tabular-nums">{r.dias_aprobados}</Td>
                <Td className="text-xs">
                  {Object.entries(r.por_tipo)
                    .map(([tipo, dias]) => `${tipo}: ${dias}`)
                    .join(" · ") || "—"}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      )}
    </>
  )
}
